import { useEffect } from 'react'
import { toastManager, type ToastDismissReason, type ToastItem } from '@renderer/overlay/Toaster/toast-store'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

export type NotchAction =
  | 'list opened'
  | 'list closed'
  | 'drafting finished'
  | 'open meeting'
  | 'ask opened'
  | 'ask submitted'
  | 'menu item'
  | 'toast shown'
  | 'toast clicked'
  | 'toast closed'
  | 'setup call'

/** Every notch interaction is one `Notch Action` event, discriminated by `action`, like `Audio Note Action`. */
export function trackNotchAction(action: NotchAction, properties?: Record<string, unknown>) {
  AmplitudeRenderer.trackEvent('Notch Action', { action, ...properties })
}

export const STAGED_ACTIONS_TOAST_TYPE = 'staged actions'

/** What every event about a notch toast carries: its type, whether it's staged actions, and its own analytics. */
export function notchToastProperties(toast: ToastItem): Record<string, unknown> {
  const { type = 'unnamed', ...rest } = toast.analytics ?? {}
  return { ...rest, toastType: type, isStagedAction: type === STAGED_ACTIONS_TOAST_TYPE }
}

interface NotchToastVisit {
  shownAt: number
  clickCount: number
  lastControl?: string
}

/** Toasts the notch has displayed, by id, until they're dismissed. */
const visits = new Map<string, NotchToastVisit>()

export function trackNotchToastShown(toast: ToastItem) {
  if (visits.has(toast.id)) return
  visits.set(toast.id, { shownAt: Date.now(), clickCount: 0 })
  trackNotchAction('toast shown', notchToastProperties(toast))
}

export function trackNotchToastClicked(toast: ToastItem, control: string, properties?: Record<string, unknown>) {
  const visit = visits.get(toast.id)
  if (visit) {
    visit.clickCount += 1
    visit.lastControl = control
  }
  trackNotchAction('toast clicked', {
    ...notchToastProperties(toast),
    control,
    visibleMs: visit ? Date.now() - visit.shownAt : undefined,
    ...properties,
  })
}

function trackNotchToastClosed(toast: ToastItem, reason: ToastDismissReason | undefined) {
  const visit = visits.get(toast.id)
  // A toast the notch never displayed (queued behind another, or in the corner) didn't close from the notch
  if (!visit) return
  visits.delete(toast.id)
  trackNotchAction('toast closed', {
    ...notchToastProperties(toast),
    // Unset means its body dismissed it, which follows a click when there was one
    reason: reason ?? (visit.lastControl ? 'clicked' : 'other'),
    visibleMs: Date.now() - visit.shownAt,
    clickCount: visit.clickCount,
    lastControl: visit.lastControl,
  })
}

/** Reports how each toast the notch displayed went away. Mount once, where the notch lives. */
export function useTrackNotchToastClosures() {
  useEffect(() => toastManager.onDismissed(trackNotchToastClosed), [])
}
